/**
 * 🔪 **KILLING A SHEEP — D125, 2026-09-25.**
 *
 * 🎯 **haugur chose every verb, one question each:**
 *
 * | | His choice |
 * |---|---|
 * | 🔪 **With what** | **a knife** — the `hnifur`, made at the bench |
 * | ❤️ **How** | **several hits, and it RUNS** — *"a sheep does not run away from you until you hit it"* |
 * | 💀 **Then** | **it falls over**, and the meat goes **straight into your pack** |
 * | 🔒 **Whose** | **wild sheep, and your own** — never somebody else's |
 *
 * 🍂 **And D123's numbers:** *"if you kill a sheep you get 1 wool and one meat,
 * unless you kill them after 1 Okt to 1 Des when they come back from gracing,
 * then you get 3 wool 3 meat."* ⚠️ **The wool is a GÆRA** — a skin with the
 * fleece on — by his own naming: *"4, gæra."* Shearing, not built, gives `ull`.
 *
 * ## 🔴 Where each rule lives, and why here
 *
 * **Everything that is arithmetic is in this file**, so a test reaches it
 * without a server — `server/index.ts` cannot be imported without a database,
 * which `carry.ts` records as how the `players` table sat empty for weeks. The
 * server does the three things that need the world: **who is holding what,
 * whose sheep it is, and what goes into whose pack.**
 *
 * 📖 **Minecraft, for the numbers that came from it** — `Minecraft/18` §1.2
 * (a sheep has **8 health**, and `panic` is its top goal), `Minecraft/05` §4
 * (**0.5 s** in which a second hit does nothing).
 */

import { type AimTarget, aimAt } from './aim.ts';
import type { Behaviour, Creature, Vec3 } from './creature.ts';
import type { MessageKey } from './i18n.ts';
import { SHEEP_HEIGHT, SHEEP_LENGTH } from './sheepmind.ts';
import { FOX_HEIGHT, FOX_KIND, FOX_LENGTH } from './foxmind.ts';

/** 🔪 The item that kills a sheep. One key, read by both ends. */
export const KNIFE_ITEM = 'hnifur';

/**
 * 🔪 **How far a knife reaches, in metres.**
 *
 * ⚠️ **Between the hands (2.5) and the spear (3.5)** — an arm and a blade.
 * Minecraft's melee reach is 3 blocks. **A dial.**
 */
export const KNIFE_REACH = 3;

/**
 * ❤️ **How many knife strikes kill a sheep.**
 *
 * 📖 **Minecraft's sheep has 8 health and a stone sword deals 5**, so it dies on
 * the second. 🔴 **Three here, on purpose**: the whole of haugur's *"it runs"*
 * is the chase between strikes, and at two there is barely one.
 */
export const SHEEP_STRIKES = 3;

/**
 * ⏱️ **How long after a strike another one does nothing, in seconds.**
 *
 * 📖 `Minecraft/05` §4: *"the most important small mechanic in the section"*
 * — without it, clicking fast is killing fast.
 */
export const STRIKE_COOLDOWN = 0.5;

/**
 * 🩹 **How long a sheep must go unstruck before it is whole again, seconds.**
 *
 * ⚠️ **Not Minecraft's**, whose animals heal by eating. Ours need SOMETHING, or
 * a sheep struck once and abandoned carries the wound until it is forgotten —
 * and a kept sheep would carry it for ever.
 */
export const HEAL_AFTER = 60;

/** 🏃 **How long a struck sheep runs, seconds.** */
export const PANIC_SECONDS = 5;

/**
 * 🏃 **How much faster than it walks a struck sheep runs.**
 *
 * ⚠️ **3× — 2.7 m/s, not Minecraft's ×1.25.** Theirs barely leaves a walking
 * player; a real sheep sprints far faster than either. **Slower than your
 * 5.6 m/s walk, so it can always be caught** — the chase has to end.
 */
export const PANIC_PACE = 3;

/**
 * 🔄 **How much faster than it calmly turns a struck sheep spins round.**
 *
 * 🔴 **Found by a test, not chosen up front.** At its walking turn rate
 * (2.2 rad/s) a sheep struck while facing you took well over a second to turn
 * — and **ran AT you the whole time.** ×3 turns it round in about half a
 * second. ⚠️ A dial.
 */
export const PANIC_TURN = 3;

/** 🏃 How far ahead a fleeing sheep aims, metres — re-aimed if it is blocked. */
const PANIC_DISTANCE = 8;

/**
 * 💀 **How long a killed sheep lies on its side before it is gone, seconds.**
 *
 * 🎯 His choice: *"falls over, meat to pack."* The client tips it over in the
 * first half second; the rest is it lying there.
 */
export const DEATH_SECONDS = 1.5;
/* 🎯 1.5 is haugur's, 2026-09-28, having played it: *"its a bit to long, lets
   try 1.5"* — it was 2.5. Half a second to tip over, one second on its side. */

/**
 * 🐑 **Which kinds a knife can strike, and how big a target each is.**
 *
 * 🔴 **A table, and a kind not in it cannot be struck** — `carry.ts`'s rule and
 * `spear.ts`'s, pointed at a third verb. **Nothing becomes killable by
 * omission.**
 */
/* 🦊 T288 — a function rather than a Set built at load, because `foxmind.ts`
   imports `sheepmind.ts`, which imports this file: a table built when the
   module loads would read `FOX_KIND` before it exists. */
const STRIKABLE = { has: (kind: string): boolean => kind === 'kind' || kind === FOX_KIND };

/**
 * ❤️ **How many strikes kill this kind.** A sheep three; 🦊 **a fox ONE** —
 * decision 134: *"one strike if asleep"*. An awake fox is never in reach.
 */
export function strikesToKill(kind: string): number {
  return kind === FOX_KIND ? 1 : SHEEP_STRIKES;
}

/**
 * How big a target a kind is. ⚠️ **A function, read at CALL time, and that is
 * load-bearing**: `sheepmind.ts` imports {@link panic} from here while this
 * file imports its sizes, and a table built when the module loads would read
 * `SHEEP_LENGTH` before it exists.
 */
function radiusOf(kind: string): number {
  if (kind === FOX_KIND) return FOX_LENGTH * 0.5;
  return kind === 'kind' ? SHEEP_LENGTH * 0.5 : 0;
}

/** Can this kind be struck with a knife at all? */
export function isStrikable(kind: string): boolean {
  return STRIKABLE.has(kind);
}

/**
 * 🔪 **The creature under your crosshair that a knife would strike, or
 * `undefined`.** Both ends run it — the id on the wire is a proposal, never a
 * fact (`carry.ts`).
 *
 * ⚠️ **Aimed at the middle of the body**, not the feet — the half-metre fault
 * `carryTargetFrom` was written to avoid.
 */
export function knifeTargetFrom(
  eye: Vec3,
  look: Vec3,
  targets: readonly AimTarget[],
  reach: number = KNIFE_REACH,
): AimTarget | undefined {
  const hit = aimAt(eye, look, targets.map(toMiddle), reach, (t) =>
    STRIKABLE.has(t.kind) ? radiusOf(t.kind) : 0,
  );
  if (hit === undefined) return undefined;
  return targets.find((t) => t.id === hit.id);
}

/** 📏 A land animal's aim point — the middle of its body, not its feet. */
function toMiddle(t: AimTarget): AimTarget {
  if (t.kind === 'kind') return { ...t, y: t.y + SHEEP_HEIGHT * 0.5 };
  if (t.kind === FOX_KIND) return { ...t, y: t.y + FOX_HEIGHT * 0.4 };
  return t;
}

/**
 * 👀 **The land animal under your crosshair, for the "Looking at" line** —
 * and `near`, how far along the crosshair its NEAR SIDE is, so the caller can
 * let a nearer block win (you must not read a fox through a turf wall).
 *
 * 🔴 **The near side, not the middle.** A sheep grazing IN scrub stands inside
 * a block the aim can hit, and that block's edge is nearer than the sheep's
 * middle — measured from the middle, the scrub would win and the sheep line
 * would vanish in birch country. A wall a fox stands behind is still a whole
 * block nearer than the fox's near side.
 *
 * 🎯 haugur, 2026-10-05: *"when i look at a fox it does not say it in Looking
 * at."* 🔴 **The line only ever asked {@link carryTargetFrom}** — which knows
 * sheep alone, by design — so a fox was looked straight through and the grass
 * behind it was named. ✅ **Every kind with a body here is a candidate**, at
 * the block reach, because *"Looking at"* means the same 6 m for both.
 *
 * ⚠️ **Shares the knife's body table today** (sheep and fox), through
 * {@link radiusOf} — a third land animal must be added there, and this will
 * see it.
 */
export function lookTargetFrom(
  eye: Vec3,
  look: Vec3,
  targets: readonly AimTarget[],
  reach: number,
): { readonly target: AimTarget; readonly near: number } | undefined {
  const lifted = targets.map(toMiddle);
  const hit = aimAt(eye, look, lifted, reach, (t) => radiusOf(t.kind));
  if (hit === undefined) return undefined;
  const along = (hit.x - eye.x) * look.x + (hit.y - eye.y) * look.y + (hit.z - eye.z) * look.z;
  const target = targets.find((t) => t.id === hit.id);
  return target === undefined ? undefined : { target, near: along - radiusOf(hit.kind) };
}

/**
 * 🔒 **May this player strike this creature?** — 🎯 *"wild and your own."*
 *
 * | Creature | Answer |
 * |---|---|
 * | **wild** — nobody keeps it | yes |
 * | **kept, owner unknown** — kept by an anonymous session (development) | yes |
 * | **kept, owned by you** | yes |
 * | **kept, owned by somebody else** | **no** |
 *
 * ⚠️ **An anonymous player (`null`) may not strike anybody's owned sheep** —
 * not having a name is not the same as being the owner.
 */
export function mayStrike(self: Creature, userId: string | null): boolean {
  if (!self.kept || self.owner === undefined) return true;
  return self.owner === userId;
}

/**
 * 💀 **Would a strike NOW be the killing one?**
 *
 * 🔴 **Asked BEFORE the blow lands**, so the server can refuse a kill into a
 * full pack rather than drop the meat nowhere. ⚠️ **It must read the same
 * rules {@link strike} applies** — dead, cooldown, healing — or the two answer
 * one question two ways.
 */
export function strikeKills(self: Creature, now: number): boolean {
  if (isDead(self)) return false;
  const last = self.memory.struckAt;
  if (last !== undefined && now - last < STRIKE_COOLDOWN) return false;
  const healed = last !== undefined && now - last >= HEAL_AFTER;
  const strikes = healed ? 0 : (self.memory.strikes ?? 0);
  return strikes + 1 >= strikesToKill(self.kind);
}

/** 💀 Has this creature been killed and is only lying there now? */
export function isDead(self: Creature): boolean {
  return self.memory.deadAt !== undefined;
}

/**
 * 🔪 **What one strike does.**
 *
 * | Answer | Meaning |
 * |---|---|
 * | `dead` | already killed — nothing happens |
 * | `tooSoon` | inside {@link STRIKE_COOLDOWN} — nothing happens, and the knife is NOT worn |
 * | `hurt` | struck, and now running from `from` |
 * | `killed` | the last strike — it falls over |
 *
 * 🔴 **It MUTATES the creature** — `memory` holds `strikes`, `struckAt`,
 * `fromX`/`fromZ` and `deadAt`, all numbers, because `memory` is numbers.
 * ⚠️ **Nothing here is saved to the database**: a restart heals a kept sheep,
 * which is the same answer {@link HEAL_AFTER} gives, sooner.
 *
 * @param now seconds, on the same clock the herd's `Sense.now` uses.
 */
export function strike(
  self: Creature,
  from: Vec3,
  now: number,
): 'dead' | 'tooSoon' | 'hurt' | 'killed' {
  if (isDead(self)) return 'dead';
  const m = self.memory;
  const last = m.struckAt;
  if (last !== undefined && now - last < STRIKE_COOLDOWN) return 'tooSoon';
  /* 🩹 Healed while nobody was looking. */
  if (last !== undefined && now - last >= HEAL_AFTER) m.strikes = 0;

  m.strikes = (m.strikes ?? 0) + 1;
  /* 🔴 **Every blow that LANDS, counted for ever** — what the wire carries so
     each client can flash the sheep red (protocol 39). ⚠️ Not `strikes`, which
     healing resets: a count that can go DOWN cannot say "it was hit again". */
  m.hits = (m.hits ?? 0) + 1;
  m.struckAt = now;
  m.fromX = from.x;
  m.fromZ = from.z;
  if (m.strikes >= strikesToKill(self.kind)) {
    m.deadAt = now;
    self.goal = null;
    return 'killed';
  }
  /* 🏃 Forget where it was going — it has somewhere more urgent now. */
  self.goal = null;
  return 'hurt';
}

/**
 * 🏃 **Panic — *"a sheep does not run away from you until you hit it."***
 *
 * 🔴 **The TOP of the list** — `Minecraft/18` §1.2 has `panic` at priority 1,
 * under only `float`. Nothing a sheep wants outranks being struck.
 *
 * ⚠️ **It runs AWAY from where the strike came from, not in a straight line
 * for ever.** Each time it is blocked the goal is dropped (`tickCreature` does
 * that), and the next one is aimed away again **turned up to 45° either
 * way** — so a sheep driven into a corner finds its way along the wall rather
 * than grinding at it. ⚠️ **It was 90° and a trace showed why not**: the
 * swerve came out at 88° and the sheep ran SIDEWAYS past you, not away. *An animal drifting hopefully at a wall* is the thing
 * `14_The_Animal_System.md` says haugur would photograph.
 */
export function panic(priority: number): Behaviour {
  return {
    name: 'Panic',
    priority,
    flags: ['move', 'look'],
    wants: (sense) => {
      const at = sense.self.memory.struckAt;
      return at !== undefined && !isDead(sense.self) && sense.now - at < PANIC_SECONDS;
    },
    act(sense) {
      const self = sense.self;
      if (self.goal !== null) {
        return { goal: self.goal, pace: PANIC_PACE, turn: PANIC_TURN, lookAt: null };
      }
      const fx = self.memory.fromX ?? self.at.x;
      const fz = self.memory.fromZ ?? self.at.z;
      let away = Math.atan2(self.at.z - fz, self.at.x - fx);
      if (!Number.isFinite(away) || (fx === self.at.x && fz === self.at.z)) away = self.heading;
      const turn = (self.rng() - 0.5) * (Math.PI / 2);
      const angle = away + turn;
      return {
        goal: {
          x: self.at.x + Math.cos(angle) * PANIC_DISTANCE,
          y: self.at.y,
          z: self.at.z + Math.sin(angle) * PANIC_DISTANCE,
        },
        pace: PANIC_PACE,
        turn: PANIC_TURN,
        lookAt: null,
      };
    },
  };
}

/**
 * 🍂 **What a killed sheep gives, on this date** — D123.
 *
 * **1 October to 1 December, BOTH ENDS INCLUDED: three meat and three gæra.**
 * Every other day: one and one. 🎯 *"after 1 Okt to 1 Des when they come back
 * from gracing"* — **réttir**, when the sheep come down from the highlands fat
 * after a summer of free grazing.
 *
 * ⚠️ **1 December included** is the Project Manager's reading of *"to 1 Des"*,
 * stated in the proposal haugur said yes to.
 *
 * @param month 1–12, the GAME's calendar (`WorldTime.month`), never the wall
 *   clock.
 */
export function slaughterYield(month: number, dayOfMonth: number): { meat: number; skins: number } {
  const n = isRettir(month, dayOfMonth) ? 3 : 1;
  return { meat: n, skins: n };
}

/**
 * 🍂 **Is it réttir — the autumn window — on this game date?** T257.
 *
 * **1 October to 1 December, both ends included.** 🔴 **The ONE place the
 * window is written down**: the yield above, the note after a kill (server) and
 * the notes when it opens and closes (client, `notices.ts`) all ask this, so
 * they can never disagree about which days count.
 *
 * @param month 1–12, the GAME's calendar, never the wall clock.
 */
export function isRettir(month: number, dayOfMonth: number): boolean {
  return month === 10 || month === 11 || (month === 12 && dayOfMonth === 1);
}

/**
 * 🍂 **What the notice line says after a kill** — T257, haugur's *"1"*.
 *
 * 🔴 **Without it a player kills all year at a third of the value and never
 * learns why** — the reward is invisible, and so is its absence. So a grown
 * sheep says what it gave, and outside réttir what réttir would have given.
 *
 * ⚠️ **A lamb and a fox say nothing**: their yields are the same in every
 * month, so there is no window to teach. Returns `null` then.
 */
export function killNote(
  kind: string,
  lamb: boolean,
  month: number,
  dayOfMonth: number,
): { key: MessageKey; values: { meat: number; autumn: number } } | null {
  if (kind !== 'kind' || lamb) return null;
  return {
    key: isRettir(month, dayOfMonth) ? 'game.notice.killedRettir' : 'game.notice.killedOffSeason',
    values: { meat: slaughterYield(month, dayOfMonth).meat, autumn: slaughterYield(10, 1).meat },
  };
}

/** 🥩 The raw meat item. */
export const MEAT_ITEM = 'kindakjot';
/** 🐑 The sheepskin item. */
export const SKIN_ITEM = 'gaera';
